import { useTranslation } from 'react-i18next';
import { CheckBox, CheckBoxProps, CircularProgress, Icon, Tooltip } from '@anytag/shared-atoms';
import { cn } from '@anytag/shared-utils';

export interface WidgetSelectButtonProps extends Pick<CheckBoxProps, 'help' | 'customTooltipProps'> {
  checked?: boolean;
  loading?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  type?: 'default' | 'checkbox';
  className?: string;
}

export const WidgetSelectButton = ({
  help,
  onClick,
  className,
  customTooltipProps,
  disabled = false,
  checked = false,
  loading = false,
  type = 'default'
}: WidgetSelectButtonProps) => {
  const { t } = useTranslation();
  const isCheckBox = type === 'checkbox';
  const customHelp =
    help || (isCheckBox ? t(!checked ? 'Select' : 'Unselect') : t(`Button.${checked ? 'Remove' : 'Add'}`));

  const handleClick = () => {
    if (disabled) {
      return;
    }

    onClick?.();
  };

  return isCheckBox ? (
    <div
      className={cn(
        'box-border flex h-full w-8 flex-none items-center justify-center border-r-border bg-white px-2',
        className
      )}
    >
      <CheckBox
        help={customHelp}
        checked={checked}
        onClick={handleClick}
        disabled={disabled || loading}
        customTooltipProps={customTooltipProps}
      />
    </div>
  ) : (
    <Tooltip
      place="top"
      className="h-full"
      help={customHelp}
      disabled={disabled}
      withinPortal={customTooltipProps?.withinPortal ?? true}
      {...customTooltipProps}
    >
      <button
        onClick={handleClick}
        className={cn(
          'flex h-full w-10 flex-none items-center justify-center border-r-border bg-white transition-colors duration-300',
          {
            'bg-active': isCheckBox,
            'bg-disabled opacity-50': disabled,
            'cursor-pointer hover:bg-menu-item-hover': !disabled
          },
          className
        )}
      >
        {loading ? (
          <CircularProgress size={18} thickness={0.3} className={checked ? 'bg-white' : 'bg-black'} />
        ) : (
          <Icon size={12} icon={checked ? 'check' : 'plus'} variant={checked ? 'white' : 'gray'} />
        )}
      </button>
    </Tooltip>
  );
};
